import { useMemo, useState } from "react"
import { formatDateTime } from "atlas_forecasting_app/lib/format"
import { useContainerWidth } from "atlas_forecasting_app/hooks/use_container_width"
import { ChartTooltip } from "atlas_forecasting_app/components/charts/chart_tooltip"
import { buildHistoryTimeAxis, buildLinePath } from "atlas_forecasting_app/components/charts/history_utils"

export function MultiSeriesHistoryLineChart({
  series,
  height = 260,
  yDomain = null,
  yTicks = null,
  yTickFormatter = (v) => String(v),
  pointFormatter = (v) => String(v),
  selectedForecastId = null,
  onSelectForecastId = null,
}) {
  const [ref, width] = useContainerWidth()
  const [tooltip, setTooltip] = useState(null)
  const [hover, setHover] = useState(null)

  const baseBottomMargin = 34

  const xValues = useMemo(() => {
    const xs = new Set()
    for (const s of series || []) {
      for (const pt of s.points || []) {
        if (Number.isFinite(pt.x)) xs.add(pt.x)
      }
    }
    return Array.from(xs).sort((a, b) => a - b)
  }, [series])

  const xAxis = useMemo(() => buildHistoryTimeAxis(xValues, width), [xValues, width])
  const hasMultilineXAxisLabels = xAxis.labelValues.some((entry) => entry.multiline && entry.secondary)
  const margin = { top: 16, right: 16, bottom: hasMultilineXAxisLabels ? 48 : baseBottomMargin, left: 56 }
  const innerW = Math.max(10, (width || 0) - margin.left - margin.right)
  const innerH = Math.max(10, height - margin.top - margin.bottom)
  const xMin = xAxis.tickValues.length ? xAxis.tickValues[0] : xValues.length ? xValues[0] : 0
  const xMax = xAxis.tickValues.length ? xAxis.tickValues[xAxis.tickValues.length - 1] : xValues.length ? xValues[xValues.length - 1] : 1
  const xRange = xMax - xMin

  const xFor = (x) => (xRange === 0 ? margin.left + innerW / 2 : margin.left + innerW * ((x - xMin) / xRange))

  const computedYDomain = useMemo(() => {
    if (Array.isArray(yDomain) && yDomain.length === 2 && yDomain.every((n) => Number.isFinite(n))) return yDomain

    let min = Infinity
    let max = -Infinity
    for (const s of series || []) {
      for (const pt of s.points || []) {
        if (pt.y == null || !Number.isFinite(pt.y)) continue
        min = Math.min(min, pt.y)
        max = Math.max(max, pt.y)
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1]
    if (min === max) return [min - 1, max + 1]
    const pad = (max - min) * 0.08
    return [min - pad, max + pad]
  }, [series, yDomain])

  const [yMin, yMax] = computedYDomain
  const yRange = yMax - yMin
  const yFor = (y) => (yRange === 0 ? margin.top + innerH / 2 : margin.top + innerH * (1 - (y - yMin) / yRange))

  const computedYTicks = useMemo(() => {
    if (Array.isArray(yTicks) && yTicks.length > 0) return yTicks
    const ticks = []
    const count = 4
    for (let i = 0; i <= count; i += 1) ticks.push(yMin + (yRange || 1) * (i / count))
    return ticks
  }, [yTicks, yMin, yRange])

  const selectedX = useMemo(() => {
    if (!selectedForecastId) return null
    for (const s of series || []) {
      const pt = (s.points || []).find((point) => point.forecastId === selectedForecastId)
      if (pt && Number.isFinite(pt.x)) return pt.x
    }
    return null
  }, [series, selectedForecastId])

  const hoverX = useMemo(() => {
    if (!hover?.forecastId) return null
    for (const s of series || []) {
      const pt = (s.points || []).find((point) => point.forecastId === hover.forecastId)
      if (pt && Number.isFinite(pt.x)) return pt.x
    }
    return null
  }, [series, hover])

  const onSvgLeave = () => {
    setTooltip(null)
    setHover(null)
  }

  const onPointMove = (event, pt, s) => {
    const svgEl = event.currentTarget.ownerSVGElement || event.currentTarget
    const rect = svgEl.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    setHover({ forecastId: pt.forecastId, seriesKey: s.key })
    setTooltip({
      x,
      y,
      title: s.label,
      body: `${formatDateTime(pt.x)}\n${pointFormatter(pt.y)}`,
    })
  }

  const onPointClick = (pt) => {
    if (!onSelectForecastId || !pt?.forecastId) return
    onSelectForecastId(pt.forecastId === selectedForecastId ? null : pt.forecastId)
  }

  return (
    <div ref={ref} className="af-chart" style={{ height: `${height}px` }}>
      {width > 0 ? (
        <svg width={width} height={height} onMouseLeave={onSvgLeave}>
          {computedYTicks.map((tick, idx) => {
            const y = yFor(tick)
            return (
              <g key={idx}>
                <line
                  x1={margin.left}
                  y1={y}
                  x2={margin.left + innerW}
                  y2={y}
                  className={idx === 0 || idx === computedYTicks.length - 1 ? "af-chart-grid" : "af-chart-grid-soft"}
                />
                <text x={margin.left - 8} y={y + 4} textAnchor="end" className="af-chart-axis-muted">
                  {yTickFormatter(tick)}
                </text>
              </g>
            )
          })}

          {xAxis.tickValues.map((tick, idx) => {
            const x = xFor(tick)
            return (
              <g key={idx}>
                <line
                  x1={x}
                  y1={margin.top + innerH - 4}
                  x2={x}
                  y2={margin.top + innerH + 8}
                  stroke="rgba(73, 80, 87, 0.42)"
                  strokeWidth="1.25"
                  shapeRendering="crispEdges"
                />
              </g>
            )
          })}

          {xAxis.labelValues.map((entry, idx) => {
            const x = xFor(entry.x)
            const labelY = hasMultilineXAxisLabels ? height - 22 : height - 10
            const combinedLabel = entry.secondary && !entry.multiline ? `${entry.primary} ${entry.secondary}` : null
            return (
              <text key={`label_${idx}`} x={x} y={labelY} textAnchor="middle" className="af-chart-axis-muted">
                {combinedLabel ? (
                  combinedLabel
                ) : (
                  <>
                    <tspan x={x} dy="0">
                      {entry.primary}
                    </tspan>
                    {entry.secondary ? (
                      <tspan x={x} dy="12">
                        {entry.secondary}
                      </tspan>
                    ) : null}
                  </>
                )}
              </text>
            )
          })}

          {selectedX != null ? (
            <line x1={xFor(selectedX)} y1={margin.top} x2={xFor(selectedX)} y2={margin.top + innerH} stroke="rgba(134, 142, 150, 0.35)" strokeDasharray="4 4" />
          ) : null}
          {hoverX != null && hoverX !== selectedX ? (
            <line x1={xFor(hoverX)} y1={margin.top} x2={xFor(hoverX)} y2={margin.top + innerH} stroke="rgba(134, 142, 150, 0.24)" strokeDasharray="2 4" />
          ) : null}

          {(series || []).map((s) => {
            const d = buildLinePath(s.points || [], xFor, yFor)
            return d ? <path key={s.key} d={d} stroke={s.color} strokeWidth={s.strokeWidth || 2.2} fill="none" opacity={0.92} /> : null
          })}

          {(series || []).flatMap((s) =>
            (s.points || []).map((pt) => {
              if (pt.y == null || !Number.isFinite(pt.y) || !Number.isFinite(pt.x)) return null
              const x = xFor(pt.x)
              const y = yFor(pt.y)
              const isSelected = !!selectedForecastId && pt.forecastId === selectedForecastId
              const isHover = hover?.forecastId === pt.forecastId && hover?.seriesKey === s.key
              const r = isSelected ? 5.2 : isHover ? 5 : 4.2
              const fill = isSelected || isHover ? s.color : "rgba(255,255,255,0.92)"
              const stroke = isSelected || isHover ? "rgba(255,255,255,0.98)" : s.color

              return (
                <circle
                  key={`${s.key}_${pt.forecastId}`}
                  data-testid={`history-point-${pt.forecastId}`}
                  cx={x}
                  cy={y}
                  r={r}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={2}
                  style={{ cursor: onSelectForecastId ? "pointer" : "default" }}
                  onMouseMove={(event) => onPointMove(event, pt, s)}
                  onClick={() => onPointClick(pt)}
                />
              )
            }),
          )}
        </svg>
      ) : null}
      <ChartTooltip tooltip={tooltip} containerWidth={width} containerHeight={height} />
    </div>
  )
}
